import { Icon, Navigation } from "@shopify/polaris"
import {
  AffiliateMajor,
  AnalyticsMajor,
  ArrowLeftMinor,
  ArrowRightMinor,
  BlogMajor,
  ColumnWithTextMajor,
  DataVisualizationMajor,
  DnsSettingsMajor,
  ExploreImagesMajor,
  HintMajor,
  NoteMajor,
  ReplayMinor,
  ReportsMajor,
  ShareMinor,
  ThemeEditMajor,
  TransactionMajor,
  WandMajor,
} from "@shopify/polaris-icons"
import {
  FAKE_SIMULATION_FEATURE,
  GHG_RECOMMENDATION_FEATURE,
  REF_DB_FEATURE,
  useFeatureFlag,
} from "hooks/useFeatureFlag"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppContext } from "components/AppContext"
import { useLocation } from "react-router-dom"
import PropTypes from "prop-types"
import getITDomainUrl from "services/navigation/getITDomainUrl"
import disabledUnderstandITDomain from "services/navigation/disabledUnderstandITDomain"
import { useITDomainTabs } from "components/ITDomainTabs"
import NewBadge from "components/widgets/NewBadge"
import { showMaturity } from "services/score/maturity/showMaturity"
import getLatestVersion from "services/changelog/getLatestVersion"

const latestVersion = getLatestVersion()

const NavigationBar = ({ collapsedNavigation, toggleCollapsedNavigation }) => {
  const { t } = useTranslation()
  const location = useLocation()

  const { ifFeatureActive, isFeatureActive } = useFeatureFlag()
  const {
    updateObjectiveNavigation,
    objectiveUri,
    disabledFeatures,
    envScore,
    currentRootNode,
    currentNode,
    currentOrganization,
  } = useAppContext()

  const [currentPath, setCurrentPath] = useState(location.pathname)
  useEffect(() => setCurrentPath(location.pathname), [location])

  const showWebsitesAnalysis = !!envScore?.websites_charts
  // Explore > Websites: only when at least one page has a v2 (detailed) audit.
  // Legacy-only orgs carry no detailed fields and must not see the sub-category.
  const showExploreWebsites =
    envScore?.website_analyse?.audit_websites?.length > 0
  const hasCorporateItFlexibility =
    currentRootNode?.has_corporate_it_flexibility_subscription
  const disabledProviderImprovePage =
    currentRootNode?.is_provider && !showWebsitesAnalysis

  const shouldShowMaturity = showMaturity(
    currentOrganization,
    currentRootNode,
    currentNode,
    isFeatureActive
  )

  const { allTabsHidden } = useITDomainTabs({
    envScore,
    currentNode,
  })

  const buildImproveNavigationItems = () => {
    const items = []

    if (ifFeatureActive(GHG_RECOMMENDATION_FEATURE)) {
      items.push({
        url: `/improve/ghg/${objectiveUri}`,
        selected: currentPath.startsWith("/improve/"),
        disabled: disabledFeatures.improveGHG,
        label: t("common:menu.improve.ghg"),
      })
    }

    if (shouldShowMaturity) {
      items.push({
        url: `/improve/maturity/${objectiveUri}`,
        selected: currentPath.startsWith("/improve/maturity"),
        disabled: disabledFeatures.improveMaturity,
        label: t("common:menu.improve.maturity"),
      })
    }

    if (showWebsitesAnalysis) {
      items.push({
        url: `/improve/websites`,
        selected: currentPath.startsWith("/improve/websites"),
        disabled: disabledFeatures.improveWebsite,
        label: t("common:menu.improve.website"),
      })
    }

    return items
  }

  if (location.pathname === "/unauthorized") {
    return null
  }

  return (
    <Navigation location="/">
      <div>
        <button
          className="navigation-toggle"
          onClick={toggleCollapsedNavigation}
        >
          <Icon
            source={collapsedNavigation ? ArrowRightMinor : ArrowLeftMinor}
          />
        </button>
        <Navigation.Section
          items={[
            {
              label: t("common:menu.dashboard"),
              icon: DataVisualizationMajor,
              url: "/dashboard",
              selected: currentPath.startsWith("/dashboard"),
              disabled: disabledFeatures.score,
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.organize"),
              icon: AffiliateMajor,
              url: "/organize",
              selected: currentPath.startsWith("/organize"),
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.collect"),
              icon: BlogMajor,
              url: `/collect/${objectiveUri}`,
              selected: currentPath.startsWith("/collect"),
              disabled: disabledFeatures.collect,
              subNavigationItems: [
                {
                  url: `/collect/overview/environmental`,
                  selected: currentPath.startsWith("/collect/overview"),
                  label: t("common:menu.data_quality"),
                },
              ],
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.assess"),
              icon: DataVisualizationMajor,
              url: `/score/${objectiveUri}`,
              onClick: () =>
                currentPath.startsWith("/score") &&
                updateObjectiveNavigation(null),
              selected: currentPath.startsWith("/score"),
              disabled: disabledFeatures.score,
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.analyze.title"),
              icon: AnalyticsMajor,
              url: getITDomainUrl(
                currentNode,
                disabledFeatures,
                `/analyze/it-domain/${objectiveUri}`,
                allTabsHidden
              ),
              selected: currentPath.startsWith("/analyze"),
              disabled:
                disabledFeatures.envScore && disabledFeatures.historization,
              subNavigationItems: [
                {
                  url: getITDomainUrl(
                    currentNode,
                    disabledFeatures,
                    `/analyze/it-domain/${objectiveUri}`
                  ),
                  selected: currentPath.startsWith("/analyze/it-domain"),
                  label: t("common:menu.analyze.it_domain"),
                  disabled: disabledUnderstandITDomain(
                    currentNode,
                    disabledFeatures,
                    allTabsHidden
                  ),
                },
                ...(showWebsitesAnalysis && !currentNode?.has_websites_only
                  ? [
                      {
                        label: t("common:menu.analyze.websites"),
                        url: `/analyze/websites`,
                        selected: currentPath.startsWith("/analyze/websites"),
                      },
                    ]
                  : []),
                {
                  url: `/analyze/history/${objectiveUri}`,
                  selected: currentPath.startsWith("/analyze/history"),
                  label: t("common:menu.analyze.historic"),
                  disabled: disabledFeatures.historization,
                },
                {
                  url: "/analyze/ghg-scopes/",
                  selected: currentPath.startsWith("/analyze/ghg-scopes"),
                  label: t("common:menu.analyze.ghg"),
                  disabled: disabledFeatures.envScore,
                },
              ],
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.explore"),
              icon: ExploreImagesMajor,
              url: "/explore",
              selected: currentPath.startsWith("/explore"),
              disabled: disabledFeatures.envScore,
              subNavigationItems: showExploreWebsites
                ? [
                    {
                      label: t("common:menu.explore_websites"),
                      url: "/explore/websites",
                      selected: currentPath.startsWith("/explore/websites"),
                    },
                  ]
                : [],
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.compare"),
              icon: TransactionMajor,
              url: `/compare/environmental`,
              selected: currentPath.startsWith("/compare"),
              disabled: disabledFeatures.compare,
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.improve.title"),
              icon: WandMajor,
              url:
                currentRootNode?.is_provider || hasCorporateItFlexibility
                  ? "/improve/websites"
                  : isFeatureActive(GHG_RECOMMENDATION_FEATURE)
                    ? `/improve/ghg/${objectiveUri}`
                    : `/improve/maturity/${objectiveUri}`,
              selected: currentPath.startsWith("/improve"),
              disabled:
                disabledFeatures.improve ||
                disabledProviderImprovePage ||
                (hasCorporateItFlexibility && !showWebsitesAnalysis),
              subNavigationItems: buildImproveNavigationItems(),
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.share"),
              icon: ShareMinor,
              url: "/share",
              selected: currentPath.startsWith("/share"),
              disabled: disabledFeatures.envScore,
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.replay"),
              icon: ReplayMinor,
              url: "/replay",
              selected: currentPath.startsWith("/replay"),
              disabled: disabledFeatures.replay,
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.simulate"),
              url: "/simulate",
              selected: currentPath.startsWith("/simulate"),
              icon: ThemeEditMajor,
              disabled: !isFeatureActive(FAKE_SIMULATION_FEATURE),
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.pilot"),
              icon: ReportsMajor,
              disabled: true,
              selected: currentPath.startsWith("/pilot"),
            },
          ]}
        />
        <Navigation.Section
          items={[
            {
              label: t("common:menu.benchmark"),
              icon: ColumnWithTextMajor,
              disabled: true,
              selected: currentPath.startsWith("/benchmark"),
            },
          ]}
        />
      </div>
      <div className="Verdikt-Footer">
        <Navigation.Section
          title={t("common:menu.footer.title")}
          items={[
            {
              url: "/changelog",
              label: t("common:menu.footer.changelog"),
              icon: NoteMajor,
              selected: currentPath.startsWith("/changelog"),
            },
            {
              url: "/reference_database",
              label: t("common:menu.footer.reference_database"),
              icon: BlogMajor,
              selected: currentPath.startsWith("/reference_database"),
              disabled: !isFeatureActive(REF_DB_FEATURE),
            },
            {
              url: "/privacy",
              label: t("common:menu.footer.privacy"),
              icon: DnsSettingsMajor,
              selected: currentPath.startsWith("/privacy"),
            },
            {
              url: "/help",
              label: t("common:menu.footer.help"),
              icon: HintMajor,
              selected: currentPath.startsWith("/help"),
            },
          ]}
        />
        {latestVersion && (
          <span className="Verdikt-Version">{`v${latestVersion}`}</span>
        )}
      </div>
    </Navigation>
  )
}

NavigationBar.propTypes = {
  collapsedNavigation: PropTypes.bool.isRequired,
  toggleCollapsedNavigation: PropTypes.func.isRequired,
  isOnGlobalDashboard: PropTypes.bool,
}

export default NavigationBar
